import { useRef } from 'react';
import { useSEO, useSchemaLD } from '@/hooks/useSEO';

const benefits = [
  {
    icon: 'ri-community-line',
    title: 'Estações de trabalho que aumentam a taxa de ocupação do coworking',
    description: 'Profissionais escolhem o coworking pelo ambiente. Mesas bem projetadas, cadeiras ergonômicas e divisórias que criam privacidade sem isolar fazem o membro renovar o plano e indicar para outros.',
  },
  {
    icon: 'ri-presentation-line',
    title: 'Salas de reunião que os membros pagam para usar',
    description: 'Uma sala de reunião bem equipada — mesa imponente, cadeiras confortáveis, painel para TV e iluminação adequada — é uma fonte de receita adicional. Membros e não-membros pagam por hora para usar.',
  },
  {
    icon: 'ri-box-3-line',
    title: 'Projeto 3D gratuito do espaço completo',
    description: 'Estações de trabalho, salas privativas, sala de reunião, recepção e lounge projetados em 3D. Você aprova o layout antes de fabricar — e garante que cada metro quadrado seja aproveitado.',
  },
  {
    icon: 'ri-tools-line',
    title: 'Montagem sem interromper a operação',
    description: 'Instalação feita pela nossa equipe nos horários de menor movimento — fins de semana ou madrugada. Seus membros não são afetados e o coworking não perde receita.',
  },
];

const blocks = [
  {
    title: 'Estações de Trabalho que Fazem o Membro Renovar o Plano Todo Mês',
    text: [
      'O membro de coworking tem opções. Ele pode trabalhar em casa, em um café ou em outro espaço. O que faz ele escolher o seu coworking — e renovar o plano mês após mês — é a experiência. E a experiência começa na estação de trabalho: a mesa na altura certa, a cadeira que não dói nas costas depois de 4 horas, a divisória que cria foco sem isolar.',
      'Nossas estações de trabalho para coworking incluem mesas em MDF de alta espessura com tampo em laminado HPL resistente a riscos e manchas, passa-fios integrado para organizar cabos de notebook, carregador e fone, divisórias acústicas em vidro temperado ou MDF que criam privacidade visual sem bloquear a luz natural, cadeiras ergonômicas com regulagem de altura, apoio lombar e braços ajustáveis, e tomadas e USB embutidos na superfície da mesa para carregamento sem cabos soltos.',
    ],
    img: 'https://readdy.ai/api/search-image?query=Modern%20coworking%20space%20with%20organized%20workstations%2C%20ergonomic%20chairs%2C%20glass%20partitions%2C%20warm%20wood%20tones%2C%20natural%20lighting%2C%20professional%20open%20office%20design%2C%20collaborative%20workspace%20interior%2C%20premium%20coworking%20environment&width=800&height=500&seq=cowork_v1_1&orientation=landscape',
    reverse: false,
  },
  {
    title: 'Salas de Reunião que Geram Receita Adicional para o Coworking',
    text: [
      'Uma sala de reunião bem equipada é um produto que se vende sozinho. Membros precisam de um espaço privado para apresentações, entrevistas e reuniões com clientes. Não-membros pagam por hora para usar. Uma sala bem projetada — com mesa imponente, cadeiras confortáveis e painel para TV — pode gerar R$ 2.000 a R$ 5.000 por mês em receita adicional.',
      'Projetamos salas de reunião para coworking com mesa de reunião em MDF de alta espessura com tampo em laminado HPL ou vidro temperado, cadeiras executivas em courvin ou tecido com estrutura em alumínio, painel de TV com suporte articulado e passa-fios integrado, iluminação LED regulável que adapta o ambiente para apresentações ou reuniões informais, e divisórias em vidro temperado que permitem visibilidade do espaço sem comprometer a privacidade acústica.',
    ],
    img: 'https://readdy.ai/api/search-image?query=Modern%20coworking%20meeting%20room%20with%20conference%20table%2C%20executive%20chairs%2C%20TV%20panel%20on%20wall%2C%20glass%20partition%20walls%2C%20warm%20lighting%2C%20professional%20collaborative%20space%2C%20premium%20interior%20design&width=800&height=500&seq=cowork_v1_2&orientation=landscape',
    reverse: true,
  },
  {
    title: 'Recepção, Lounge e Salas Privativas que Completam a Experiência',
    text: [
      'O coworking moderno é mais do que mesas e cadeiras. É um ecossistema de ambientes que atendem diferentes necessidades ao longo do dia: a recepção que acolhe o membro na entrada, o lounge onde ele descansa e faz networking, as salas privativas onde ele tem foco total para calls e trabalho concentrado.',
      'Para a recepção, projetamos balcões com tampo em granito ou laminado premium e frente com o logo do coworking em relevo. Para o lounge, sofás e poltronas de design em tecido resistente, mesas de centro e bancada para café. Para as salas privativas, mesas compactas com divisórias acústicas e iluminação individual. Cada ambiente projetado para maximizar a experiência do membro e a receita do coworking.',
    ],
    img: 'https://readdy.ai/api/search-image?query=Modern%20coworking%20lounge%20area%20with%20comfortable%20sofas%2C%20coffee%20table%2C%20warm%20lighting%2C%20plants%2C%20casual%20collaborative%20space%2C%20premium%20coworking%20interior%2C%20welcoming%20atmosphere%2C%20professional%20photography&width=800&height=500&seq=cowork_v1_3&orientation=landscape',
    reverse: false,
  },
];

const galleryImages = [
  {
    src: 'https://readdy.ai/api/search-image?query=Modern%20coworking%20open%20workspace%20with%20rows%20of%20desks%2C%20ergonomic%20chairs%2C%20glass%20partitions%2C%20warm%20wood%20tones%2C%20natural%20lighting%2C%20professional%20interior&width=600&height=400&seq=cowork_g1&orientation=landscape',
    alt: 'Espaço aberto de coworking com estações de trabalho',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=Coworking%20meeting%20room%20with%20conference%20table%2C%20chairs%2C%20TV%20panel%2C%20glass%20walls%2C%20modern%20professional%20design&width=600&height=400&seq=cowork_g2&orientation=landscape',
    alt: 'Sala de reunião de coworking com mesa e painel de TV',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=Coworking%20reception%20desk%20with%20modern%20design%2C%20warm%20tones%2C%20professional%20lobby%2C%20welcoming%20atmosphere&width=600&height=400&seq=cowork_g3&orientation=landscape',
    alt: 'Recepção de coworking com balcão moderno',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=Coworking%20lounge%20area%20with%20sofas%2C%20coffee%20table%2C%20plants%2C%20casual%20collaborative%20space%2C%20warm%20lighting%2C%20modern%20interior&width=600&height=400&seq=cowork_g4&orientation=landscape',
    alt: 'Lounge de coworking com sofás e área de convivência',
  },
];

const WA_LINK = 'https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Coworking%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!';

export default function CoworkingPage() {
  const whatsappBtnRef = useRef<HTMLAnchorElement>(null);

  useSEO({
    title: 'Mobiliário para Coworking Caruaru PE | Espaços Colaborativos – Home Office Premium',
    description: 'Mobiliário para coworking e espaços colaborativos em Caruaru PE. Estações de trabalho, salas de reunião, recepção e lounge. Projeto 3D gratuito e montagem sem interromper a operação. Atendemos coworkings no Agreste e Sertão de Pernambuco.',
    keywords: 'mobiliário coworking Recife, móveis espaço colaborativo Petrolina, estação trabalho coworking Garanhuns, sala reunião coworking Caruaru, mobiliário escritório compartilhado Agreste PE, mesa coworking Pernambuco, cadeira ergonômica coworking Nordeste, divisória coworking Recife, mobiliário coworking Caruaru',
    path: '/coworking',
  });

  useSchemaLD({
    id: 'schema-coworking',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Mobiliário para Coworking e Espaços Colaborativos | Caruaru PE',
      description: 'Mobiliário para coworking: estações de trabalho, salas de reunião, recepção e lounge. Projeto 3D gratuito, equipe própria de montagem.',
      url: `${import.meta.env.VITE_SITE_URL ?? 'https://homeofficepremium.com'}/coworking`,
      about: {
        '@type': 'Service',
        name: 'Mobiliário para Coworking e Espaços Colaborativos',
        provider: {
          '@type': 'Organization',
          name: 'Home Office Premium',
          address: {
            '@type': 'PostalAddress',
            streetAddress: 'Av. Agamenon Magalhães, 1519',
            addressLocality: 'Caruaru',
            addressRegion: 'PE',
            postalCode: '55014-000',
            addressCountry: 'BR',
          },
        },
      },
    },
  });

  return (
    <div className="lp-page-wrapper min-h-screen bg-white">
      {/* Hero */}
      <section
        className="relative min-h-[640px] md:min-h-[700px] flex items-center"
        style={{
          backgroundImage: 'url(https://readdy.ai/api/search-image?query=Modern%20premium%20coworking%20space%20interior%20with%20open%20workstations%2C%20ergonomic%20chairs%2C%20glass%20partitions%2C%20warm%20wood%20tones%2C%20natural%20lighting%2C%20collaborative%20professional%20atmosphere%2C%20wide%20angle%20photography&width=1400&height=700&seq=cowork_hero&orientation=landscape)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/55 to-black/20"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-28 pb-16 w-full">
          <div className="max-w-2xl">
            <span className="inline-block bg-[#E8610A] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-5">
              Coworking · Espaços Colaborativos · Hubs
            </span>
            <h1 className="text-white text-4xl md:text-5xl font-bold leading-tight mb-4">
              Mobiliário que faz o membro renovar o plano e indicar seu coworking.
            </h1>
            <p className="text-white/85 text-lg leading-relaxed mb-3">
              Estações de trabalho ergonômicas, salas de reunião que geram receita e lounge que cria comunidade. Porque o ambiente é o produto do coworking.
            </p>
            <p className="text-white/70 text-sm mb-8">
              Projeto 3D gratuito · Montagem sem interromper a operação · Suporte local em Caruaru e Agreste
            </p>
            <a
              href={WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#E8610A] hover:bg-[#C4500A] text-white font-bold px-8 py-4 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide"
            >
              <i className="ri-whatsapp-line text-lg"></i>
              SOLICITAR ORÇAMENTO E PROJETO 3D GRATUITO
            </a>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-gray-900 text-2xl md:text-3xl font-bold">
              Por que coworkings de Caruaru escolhem a <span className="text-[#E8610A]">Home Office Premium</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((b) => (
              <div key={b.title} className="bg-white rounded-xl p-6 flex flex-col gap-4 border border-gray-200">
                <div className="w-12 h-12 flex items-center justify-center bg-[#E8610A]/10 rounded-lg">
                  <i className={`${b.icon} text-[#E8610A] text-2xl`}></i>
                </div>
                <h3 className="text-gray-900 font-bold text-base leading-snug">{b.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{b.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Content Blocks */}
      <section className="py-16 bg-[#fafafa]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-gray-900 text-2xl md:text-3xl font-normal leading-tight">
              <span className="text-[#E8610A] font-semibold">Ambientes de Coworking</span> que Retêm Membros e Geram Receita
            </h2>
            <div className="w-16 h-0.5 bg-[#E8610A] mx-auto mt-4"></div>
          </div>
          <div className="flex flex-col gap-12">
            {blocks.map((block, idx) => (
              <div key={idx} className={`flex flex-col ${block.reverse ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-8 bg-white rounded-xl p-8 border border-gray-200`}>
                <div className="w-full lg:w-1/2 h-64 md:h-72 rounded-lg overflow-hidden flex-shrink-0">
                  <img src={block.img} alt={block.title} className="w-full h-full object-cover object-top" />
                </div>
                <div className="w-full lg:w-1/2">
                  <h3 className="text-gray-900 text-xl font-bold mb-4 leading-snug">{block.title}</h3>
                  <div className="flex flex-col gap-3">
                    {block.text.map((p, i) => (
                      <p key={i} className="text-gray-600 text-sm leading-relaxed">{p}</p>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-center mt-10">
            <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#E8610A] hover:bg-[#C4500A] text-white font-bold px-10 py-4 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide">
              <i className="ri-whatsapp-line text-lg"></i>
              SOLICITAR ORÇAMENTO E PROJETO 3D GRATUITO
            </a>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-20 bg-[#1a1a1a]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-[#E8610A] text-sm font-bold uppercase tracking-widest mb-4">Sem custo e sem compromisso</p>
          <h2 className="text-white text-3xl md:text-4xl font-bold leading-tight mb-4">
            Veja seu coworking completo em 3D antes de investir.
          </h2>
          <p className="text-gray-400 text-base mb-10">
            Estações de trabalho, salas de reunião, recepção e lounge projetados em 3D. Você aprova cada detalhe antes de fabricarmos. Montagem sem interromper a operação.
          </p>
          <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold px-10 py-4 rounded-full transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide">
            <i className="ri-whatsapp-line text-lg"></i>
            QUERO MEU PROJETO 3D GRATUITO
          </a>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-16 bg-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-gray-400 text-sm mb-2">Galeria</p>
            <h2 className="text-white text-2xl md:text-3xl font-bold">
              Ambientes de <span className="text-[#E8610A]">Coworking Premium</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {galleryImages.map((img, idx) => (
              <div key={idx} className="w-full h-64 md:h-80 rounded-xl overflow-hidden">
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-gray-400 text-xs font-semibold uppercase tracking-widest mb-3">SUPORTE LOCAL · CARUARU E AGRESTE PERNAMBUCANO</p>
          <h2 className="text-gray-900 text-4xl md:text-5xl font-bold mb-4">Solicite seu orçamento</h2>
          <p className="text-gray-500 text-base mb-8">Projeto 3D gratuito incluso. Resposta em até 2 horas.</p>
          <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 justify-center bg-[#25D366] hover:bg-[#1ebe5d] text-white font-semibold px-10 py-4 rounded-full transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm mb-10">
            <i className="ri-whatsapp-line text-lg"></i>
            Chamar no WhatsApp
          </a>
          <div className="mt-10 text-gray-500 text-sm">
            <p>Av. Agamenon Magalhães, 1519 · Maurício de Nassau, Caruaru - PE · CEP 55014-000</p>
          </div>
        </div>
      </section>

      <footer className="bg-white py-8 border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-6 flex flex-col items-center gap-4">
          <img src="https://files.greatpages.com.br/arquivos/paginas_editor/98015-32d41c6ba8b126a2913863e7b342fb04.png" alt="Home Office Premium" className="h-16 w-auto object-contain" />
          <div className="flex items-center gap-4">
            <a href="https://www.instagram.com/homeoffice.premium/" target="_blank" rel="noopener noreferrer" className="cursor-pointer"><img src="https://files.greatpages.com.br/arquivos/paginas_editor/1-cc1918b73804ac63dbfe7624bf53ae73.png" alt="Instagram" className="w-8 h-8 object-contain" /></a>
            <a href="https://www.facebook.com/homeoffice.premium" target="_blank" rel="noopener noreferrer" className="cursor-pointer"><img src="https://files.greatpages.com.br/arquivos/paginas_editor/1-d39cee63d39805fee1ea3fe03b5a2e09.png" alt="Facebook" className="w-8 h-8 object-contain" /></a>
          </div>
        </div>
      </footer>

      <a ref={whatsappBtnRef} href={WA_LINK} target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 z-[9999] w-14 h-14 flex items-center justify-center bg-[#25D366] rounded-full cursor-pointer hover:scale-110 transition-transform duration-200" aria-label="WhatsApp" style={{ display: 'flex' }}>
        <i className="ri-whatsapp-line text-white text-2xl"></i>
      </a>
    </div>
  );
}
